/**
 * formFields.jsx
 *
 * Renders individual form fields based on their field definition.
 * Each field definition has: { name, type, label, required, options, size, placeholder, tooltip }
 *
 * Subcomponents accept narrowed `value` and `error` props so the memoized
 * `FormField` dispatcher can skip re-renders when other fields change.
 * The dispatcher remains backward-compatible with callers passing the full
 * `formData` / `formErrors` objects.
 */

import React, { memo } from 'react';
import { PatternFormat } from 'react-number-format';

import FileUploader from '../file_uploader/FileUploader';
import { ArrowIcon, QuestionIcon } from '../utils/icons';

// ---------------------------------------------------------------------------
// Small shared sub-components
// ---------------------------------------------------------------------------

const RequiredMark = () => <span className="required-asterisk"> *</span>;

const FieldError = ({ error }) =>
  error ? <div className="form-field-error">{error}</div> : null;

const FieldLabel = ({ field, asLegend = false }) => {
  const hasHtml = !!field.labelIsHtml;
  const className = `control-label${field.tooltip ? ' quote-tooltip' : ''}`;

  const content = (
    <>
      {hasHtml ? (
        <span dangerouslySetInnerHTML={{ __html: field.label }} />
      ) : (
        field.label
      )}
      {field.tooltip && <QuestionIcon tooltip={field.tooltip} />}
      {field.required && <RequiredMark />}
    </>
  );

  return asLegend ? (
    <legend className={className}>{content}</legend>
  ) : (
    <label className={className} htmlFor={field.name}>
      {content}
    </label>
  );
};

const FieldWrapper = ({ field, children }) => (
  <div
    className={`form-group form__input-wrapper wrapper-${field.name} field-${
      field.size ?? 'half'
    }`}
  >
    {children}
  </div>
);

// ---------------------------------------------------------------------------
// Field type components
// ---------------------------------------------------------------------------

const TextInput = ({ field, value, error, handleChange }) => (
  <FieldWrapper field={field}>
    <FieldLabel field={field} />
    <input
      name={field.name}
      id={field.name}
      type="text"
      value={value ?? ''}
      placeholder={field.placeholder ?? ''}
      onChange={handleChange}
      className="form-control input-md"
    />
    <FieldError error={error} />
  </FieldWrapper>
);

const LengthWidthInput = ({ field, formData, formErrors, error, handleChange }) => (
  <FieldWrapper field={field}>
    <FieldLabel field={field} />
    <div className="length-width-wrapper">
      <input
        name="site_length"
        id={`${field.name}_length`}
        value={formData?.site_length ?? ''}
        type="text"
        placeholder="Length"
        onChange={handleChange}
        className="form-control input-md"
        inputMode="numeric"
      />
      <input
        name="site_width"
        id={`${field.name}_width`}
        value={formData?.site_width ?? ''}
        type="text"
        placeholder="Width"
        onChange={handleChange}
        className="form-control input-md"
        inputMode="numeric"
      />
    </div>
    <FieldError error={formErrors?.site_length || formErrors?.site_width || error} />
  </FieldWrapper>
);

const EmailInput = ({ field, value, error, handleChange }) => (
  <FieldWrapper field={field}>
    <FieldLabel field={field} />
    <input
      name={field.name}
      id={field.name}
      type="email"
      value={value ?? ''}
      placeholder={field.placeholder ?? ''}
      onChange={handleChange}
      className="form-control input-md"
    />
    <FieldError error={error} />
  </FieldWrapper>
);

const PhoneInput = ({ field, value, error, handleChange }) => (
  <FieldWrapper field={field}>
    <FieldLabel field={field} />
    <PatternFormat
      name={field.name}
      id={field.name}
      type="tel"
      format="(###) ###-####"
      mask="_"
      value={value ?? ''}
      placeholder={field.placeholder ?? ''}
      onValueChange={(values) =>
        handleChange({ target: { name: field.name, value: values.value } })
      }
      className="form-control input-md"
    />
    <FieldError error={error} />
  </FieldWrapper>
);

const NumberInput = ({ field, value, error, handleChange }) => (
  <FieldWrapper field={field}>
    <FieldLabel field={field} />
    <input
      name={field.name}
      id={field.name}
      type="number"
      value={value ?? ''}
      placeholder={field.placeholder ?? ''}
      onChange={handleChange}
      className="form-control input-md"
      min={field.min}
      max={field.max}
    />
    <FieldError error={error} />
  </FieldWrapper>
);

const ZipInput = ({ field, value, error, handleChange }) => (
  <FieldWrapper field={field}>
    <FieldLabel field={field} />
    <input
      name={field.name}
      id={field.name}
      type="text"
      value={value ?? ''}
      placeholder={field.placeholder ?? ''}
      onChange={handleChange}
      className="form-control input-md"
      maxLength={10}
      inputMode="numeric"
    />
    <FieldError error={error} />
  </FieldWrapper>
);

const TextareaInput = ({ field, value, error, handleChange }) => (
  <FieldWrapper field={field}>
    <FieldLabel field={field} />
    <textarea
      name={field.name}
      id={field.name}
      value={value ?? ''}
      placeholder={field.placeholder ?? ''}
      onChange={handleChange}
      className="form-control"
      rows={4}
    />
    <FieldError error={error} />
  </FieldWrapper>
);


const SelectInput = ({ field, value, error, handleChange }) => (
  <FieldWrapper field={field}>
    <FieldLabel field={field} />
    <div className="select-wrapper select-wrapper--primary">
      <ArrowIcon />
      <select
        name={field.name}
        id={field.name}
        value={value ?? ''}
        onChange={handleChange}
        className="form-control"
      >
        {field.placeholder && (
          <option value="" disabled>
            {field.placeholder}
          </option>
        )}
        {(field.options ?? []).map((opt) =>
          typeof opt === 'object' ? (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ) : (
            <option key={opt} value={opt}>
              {opt}
            </option>
          )
        )}
      </select>
    </div>
    <FieldError error={error} />
  </FieldWrapper>
);

const RadioGroup = ({ field, value, error, handleChange }) => (
  <FieldWrapper field={field}>
    <fieldset className="fieldset-reset">
      <FieldLabel field={field} asLegend />
      <div className="radio-buttons">
        {(field.options ?? []).map((option, i) => (
          <label key={option} htmlFor={`${field.name}-${i}`}>
            <input
              type="radio"
              name={field.name}
              id={`${field.name}-${i}`}
              value={option}
              checked={value === option}
              onChange={handleChange}
            />
            {option}
          </label>
        ))}
      </div>
    </fieldset>
    <FieldError error={error} />
  </FieldWrapper>
);

const CheckboxGroup = ({ field, value, error, handleChange }) => {
  const currentValues = Array.isArray(value)
    ? value
    : value
    ? [value]
    : [];

  const handleCheckboxChange = (e) => {
    const { value: optionValue, checked } = e.target;
    const next = checked
      ? [...currentValues, optionValue]
      : currentValues.filter((v) => v !== optionValue);

    handleChange({ target: { name: field.name, value: next } });
  };

  return (
    <FieldWrapper field={field}>
      <fieldset className="fieldset-reset">
        <FieldLabel field={field} asLegend />
        <div className="radio-buttons">
          {(field.options ?? []).map((option, i) => (
            <label key={option} htmlFor={`${field.name}-${i}`}>
              <input
                type="checkbox"
                name={field.name}
                id={`${field.name}-${i}`}
                value={option}
                checked={currentValues.includes(option)}
                onChange={handleCheckboxChange}
              />
              {option}
            </label>
          ))}
        </div>
      </fieldset>
      <FieldError error={error} />
    </FieldWrapper>
  );
};

/**
 * OptInGroup is the one component that legitimately needs multi-field
 * access — it iterates over `field.fields` (e.g., email_opt_in, sms_opt_in)
 * and reads each sub-field's value. It accepts the full `formData` object,
 * which means this component will re-render on every form change. That's
 * acceptable: there's typically only one opt-in group in a form and it
 * renders only a couple of checkboxes.
 */
const OptInGroup = ({ field, formData, error, handleChange }) => {
  const handleOptInChange = (e) => {
    const { name, checked } = e.target;
    handleChange({ target: { name, value: checked ? 'Yes' : 'No' } });
  };

  return (
    <FieldWrapper field={field}>
      <fieldset className="fieldset-reset">
        <FieldLabel field={field} asLegend />
        <div className="radio-buttons opt-in-wrapper">
          {(field.fields ?? []).map((optInField) => (
            <label key={optInField.name} htmlFor={optInField.name}>
              <input
                type="checkbox"
                name={optInField.name}
                id={optInField.name}
                checked={formData?.[optInField.name] === 'Yes'}
                onChange={handleOptInChange}
              />
              {optInField.label}
            </label>
          ))}
        </div>
      </fieldset>
      <FieldError error={error} />
    </FieldWrapper>
  );
};

/**
 * File upload — shown when tax_exempt === "Yes".
 * Calls onFileSelect(file) with the raw File object; parent converts to base64.
 */
const FileInput = ({ field, error, onFileSelect }) => (
  <FieldWrapper field={field} >
    <FieldLabel field={field} />
    {onFileSelect ? (
      <FileUploader
        onFileSelect={onFileSelect}
        accept={field.accept || "*/*"}
        maxSizeMB={field.maxSizeMB || 10}

      />
    ) : (
      <div>
        <input name={field.name} 
        type="file" 
        multiple={!!field.multiple} 
        onChange={(e) => onFileSelect && onFileSelect(field.multiple ? Array.from(e.target.files || []) : (e.target.files[0] ?? null))} 
        className="form-control input-md" id={field.name} />
      </div>
    )}
    {/* <input
      name={field.name}
      id={field.name}
      type="file"
      accept={field.accept ?? 'application/pdf,.pdf'}
      onChange={(e) => onFileSelect && onFileSelect(e.target.files[0] ?? null)}
      className="form-control input-md"
    />
    {field.placeholder && (
      <div className="form-field-label">{field.placeholder}</div>
    )} */}
    <FieldError error={error} />
  </FieldWrapper>
);

const DatePickerInput = ({ field, value, handleChange }) => (
  <FieldWrapper field={field}>
    <FieldLabel field={field} />
    <input type="date" name={field.name} id={field.name} value={value ?? ''} onChange={handleChange} className="form-control input-md" />
  </FieldWrapper>
);

// ---------------------------------------------------------------------------
// Main dispatcher
// ---------------------------------------------------------------------------

/**
 * FormField accepts narrowed props (`value`, `error`) — preferred for
 * performance, since they let `React.memo` skip re-renders when only an
 * unrelated field changes.
 *
 * For backward compatibility with callers that still pass the full
 * `formData` / `formErrors` objects, the dispatcher will derive the
 * narrowed values when explicit ones aren't provided. New callers should
 * pass `value` and `error` directly.
 *
 * The opt_in_group field type still needs full `formData` access because
 * it reads multiple sibling values; the dispatcher forwards `formData`
 * to it directly.
 */
export const FormField = memo(({ field, value, error, formData, formErrors, handleChange, onFileSelect }) => {
  // Backward-compat fallback: derive narrowed props if not explicitly passed.
  const v = value !== undefined ? value : formData?.[field.name];
  const err = error !== undefined ? error : formErrors?.[field.name];

  switch (field.type) {
    case 'text':
      return <TextInput field={field} value={v} error={err} handleChange={handleChange} />;
    case 'email':
      return <EmailInput field={field} value={v} error={err} handleChange={handleChange} />;
    case 'tel':
      return <PhoneInput field={field} value={v} error={err} handleChange={handleChange} />;
    case 'number':
      return <NumberInput field={field} value={v} error={err} handleChange={handleChange} />;
    case 'zip':
      return <ZipInput field={field} value={v} error={err} handleChange={handleChange} />;
    case 'textarea':
      return <TextareaInput field={field} value={v} error={err} handleChange={handleChange} />;
    case 'select':
      return <SelectInput field={field} value={v} error={err} handleChange={handleChange} />;
    case 'length_width':
      return <LengthWidthInput field={field} formData={formData} formErrors={formErrors} error={err} handleChange={handleChange} />;
    case 'radio':
      return <RadioGroup field={field} value={v} error={err} handleChange={handleChange} />;
    case 'checkbox':
      return <CheckboxGroup field={field} value={v} error={err} handleChange={handleChange} />;
    case 'opt_in_group':
      return <OptInGroup field={field} formData={formData} error={err} handleChange={handleChange} />;
    case 'file':
      return <FileInput field={field} error={err} onFileSelect={onFileSelect} />;
    case 'date':
      return <DatePickerInput field={field} value={v} handleChange={handleChange} />;
    case 'hidden':
    default:
      return null;
  }
});

FormField.displayName = 'FormField';
